import type { DocumentFormat } from '../models/viewer-types';

/** The formats that are text at heart, whatever the name says. */
export type TextFormat = Extract<DocumentFormat, 'markdown' | 'json' | 'xml' | 'html' | 'code' | 'text' | 'msp360-log'>;

export const TEXT_FORMATS: Array<TextFormat> = ['markdown', 'json', 'xml', 'html', 'code', 'text', 'msp360-log'];

/** What was read out of the bytes, and how to show it. */
export interface TextDocument {
  type: TextFormat;
  text: string;
  encoding: string;
  language: string;
}

const languages: Record<string, string> = {
  js: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  ps1: 'plaintext',
  sql: 'sql',
  java: 'java',
  cs: 'csharp',
  c: 'cpp',
  h: 'cpp',
  cpp: 'cpp',
  hpp: 'cpp',
  cc: 'cpp',
  go: 'go',
  rs: 'rust',
  rb: 'ruby',
  php: 'php',
  css: 'css',
  scss: 'css',
  yml: 'yaml',
  yaml: 'yaml',
  toml: 'ini',
  ini: 'ini',
  cfg: 'ini',
  conf: 'ini',
  diff: 'diff',
  patch: 'diff',
  jsonc: 'json',
  vue: 'xml',
  svelte: 'xml',
  kt: 'plaintext',
  swift: 'plaintext',
  dart: 'plaintext',
  r: 'plaintext',
  bat: 'plaintext',
  cmd: 'plaintext',
  dockerfile: 'dockerfile',
  txt: 'plaintext',
  log: 'plaintext',
  csv: 'plaintext',
  tsv: 'plaintext',
  env: 'ini',
  gitignore: 'plaintext'
};

const markupExtensions = ['md', 'markdown', 'mdown', 'mkd', 'json', 'xml', 'xsd', 'xsl', 'xslt', 'rss', 'atom', 'html', 'htm', 'xhtml'];

const textNames = ['dockerfile', 'makefile', '.env', '.gitignore'];
const markdownExtensions = ['md', 'markdown', 'mdown', 'mkd'];
const binaryExtensions = ['xlsx', 'docx', 'pdf', 'xls', 'doc', 'ppt', 'pptx', 'zip'];
const plainExtensions = ['txt', 'log', 'csv', 'tsv'];

// A control character outside tab, newline and carriage return says the bytes are not text at all.
// eslint-disable-next-line no-control-regex
const controlCharacters = /[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/;

/**
 * Whether a name is known to be text. The name decides before the bytes do, so that a media signature
 * that happens to sit inside source code does not turn the file into a picture.
 *
 * @param {string} [name] Name of the document.
 * @return {boolean} True when the name is one the viewer reads as text.
 */
export function prefersText(name = ''): boolean {
  const base = (name.toLowerCase().split('/').pop() as string) || '';
  const extension = (base.split('.').pop() as string) || '';

  return !!languages[extension] || markupExtensions.includes(extension) || textNames.includes(base);
}

// What the name says the format is; null leaves the decision to the bytes.
function formatByName(extension: string, base: string, mime: string): TextFormat | null {
  if (markdownExtensions.includes(extension)) return 'markdown';

  if (extension === 'json' || /(?:\/|\+)json$/.test(mime)) return 'json';

  if (['html', 'htm', 'xhtml'].includes(extension) || mime === 'text/html' || mime === 'application/xhtml+xml') return 'html';

  if (['xml', 'xsd', 'xsl', 'xslt', 'svg', 'rss', 'atom'].includes(extension) || /(?:\/|\+)xml$/.test(mime)) return 'xml';

  if (['text/markdown', 'text/x-markdown'].includes(mime)) return 'markdown';

  if (languages[extension] || textNames.includes(base)) return plainExtensions.includes(extension) ? 'text' : 'code';

  if (/^(?:text\/|application\/(?:javascript|x-javascript|sql|yaml|x-yaml))/.test(mime)) {
    return mime === 'text/plain' ? 'text' : 'code';
  }

  return null;
}

// What the text itself starts with, once the name has said nothing.
function formatByContent(text: string): TextFormat {
  const trimmed = text.trimStart();

  if (/^[[{]/.test(trimmed)) return 'json';

  if (/^(?:<!doctype html|<html[\s>])/i.test(trimmed)) return 'html';

  if (/^(?:<\?xml|<[a-z_][\w:.-]*(?:\s|>|\/))/i.test(trimmed)) return 'xml';

  return 'text';
}

function syntaxLanguage(type: TextFormat, extension: string, base: string, mime: string): string {
  if (languages[extension]) return languages[extension];

  if (base === 'dockerfile') return 'dockerfile';

  if (type === 'html' || type === 'xml') return 'xml';

  if (type === 'markdown') return 'markdown';

  if (type === 'json') return 'json';

  return /javascript/.test(mime) ? 'javascript' : 'plaintext';
}

/**
 * Reads bytes as text, strictly: a supported encoding, and nothing binary inside. An unknown binary
 * file is never turned into HTML — it is either refused or left for another format to claim.
 *
 * @param {ArrayBuffer} buffer The bytes of the document.
 * @param {string} [name] Name of the document.
 * @param {string} [mime] Content type the server declared.
 * @param {DocumentFormat} [format] A format the caller insists on.
 * @param {string} [language] The syntax language to fall back to.
 * @return {TextDocument} What was read, or null when these bytes are not text the viewer shows.
 */
export function detectTextDocument(
  buffer: ArrayBuffer,
  name = '',
  mime = '',
  format: DocumentFormat = 'auto',
  language = ''
): TextDocument | null {
  const extension = (name.toLowerCase().split('.').pop() as string) || '';
  const base = (name.toLowerCase().split('/').pop() as string) || '';

  if (format === 'auto' && binaryExtensions.includes(extension)) return null;

  let type = TEXT_FORMATS.includes(format as TextFormat) ? (format as TextFormat) : null;

  if (!type && format !== 'auto') return null;

  if (!type) type = formatByName(extension, base, mime);

  const bytes = new Uint8Array(buffer);
  let encoding = 'utf-8';

  if (bytes[0] === 255 && bytes[1] === 254) encoding = 'utf-16le';

  if (bytes[0] === 254 && bytes[1] === 255) encoding = 'utf-16be';

  let text: string;

  try {
    text = new TextDecoder(encoding, { fatal: true }).decode(buffer);
  } catch {
    if (type) throw Error('This text encoding is not supported. Use UTF-8 or UTF-16 with a byte-order mark.');

    return null;
  }

  if (controlCharacters.test(text.slice(0, 8192))) {
    if (type) throw Error('This file contains binary data rather than supported text.');

    return null;
  }

  if (!type) type = formatByContent(text);

  return { type, text, encoding, language: language || syntaxLanguage(type, extension, base, mime) };
}
